/**
 * Service Configurator — 3-axis picker (style / size / length).
 *
 * Replaces the long flat list when bootstrap returns serviceFamilies.
 * Each style card maps to either a size × length matrix (Knotless,
 * Boho, Senegalese) or a flat service list (Locs, Lashes). Picking a
 * cell resolves to a real Square variationId via the matrix; the App's
 * commitService handles the cart-add + step advance from there.
 *
 * Design provenance: standalone Service Configurator design from
 * /Users/matt/Downloads/Salon Boss Lift - Service Configurator (standalone).html
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { BootstrapResponse } from '../types';

type ServiceFamily = NonNullable<BootstrapResponse['serviceFamilies']>[number];
type FlatFamily = NonNullable<BootstrapResponse['flatFamilies']>[number];

const FAMILY_SIZES = ['Jumbo', 'Large', 'Smedium', 'Medium', 'Small'] as const;
const FAMILY_LENGTHS = ['Shoulder', 'Midback', 'Waist', 'Butt'] as const;

type Size = (typeof FAMILY_SIZES)[number];
type Length = (typeof FAMILY_LENGTHS)[number];

interface Props {
  families: ServiceFamily[];
  flatFamilies: FlatFamily[];
  /** Currently committed service id (the App's `selectedServiceId`). */
  selectedServiceId: string | null;
  /** Highlight a service variation. App commits via Continue. */
  onSelect: (variationId: string) => void;
  /**
   * Clear the current highlight. Called when the user switches to a
   * flat family that doesn't contain the previously-highlighted
   * variation — without clearing, Continue could submit the prior
   * service while the UI shows a different family is active.
   */
  onClear?: () => void;
}

/**
 * Walk every family/flat-family in the input and find which one owns
 * a given variationId. Lets us hydrate the local axes from the App's
 * `selectedServiceId` on mount or back-nav so the UI doesn't lie
 * about what's active.
 */
function locateSelection(
  variationId: string | null,
  families: ServiceFamily[],
  flatFamilies: FlatFamily[],
): { styleId: string; size?: string; length?: string } | null {
  if (!variationId) return null;
  for (const f of families) {
    for (const [size, lengths] of Object.entries(f.matrix)) {
      if (!lengths) continue;
      for (const [length, cell] of Object.entries(lengths)) {
        if (cell?.variationId === variationId) {
          return { styleId: f.id, size, length };
        }
      }
    }
  }
  for (const f of flatFamilies) {
    if (f.services.some((s) => s.variationId === variationId)) {
      return { styleId: f.id };
    }
  }
  return null;
}

const formatPrice = (cents: number): string => '$' + Math.round(cents / 100);

function pickDefaultSize(family: ServiceFamily): Size | null {
  // Prefer Medium when available — most-common braid size, anchors a
  // believable summary card on first paint. Fall back to the first
  // size the family actually has.
  if (family.matrix.Medium) return 'Medium';
  for (const s of FAMILY_SIZES) {
    if (family.matrix[s]) return s;
  }
  return null;
}

function pickDefaultLength(
  family: ServiceFamily,
  size: Size | null,
): Length | null {
  if (!size) return null;
  const row = family.matrix[size] ?? {};
  if (row.Midback) return 'Midback';
  for (const l of FAMILY_LENGTHS) {
    if (row[l]) return l;
  }
  return null;
}

export function ServiceConfigurator({
  families,
  flatFamilies,
  selectedServiceId,
  onSelect,
  onClear,
}: Props) {
  // Combined card list — one per family, matrix or flat. Render order
  // is matrix-first (the catalog's primary flows), then flats.
  const styles = useMemo(
    () => [
      ...families.map((f) => ({ kind: 'matrix' as const, family: f })),
      ...flatFamilies.map((f) => ({ kind: 'flat' as const, family: f })),
    ],
    [families, flatFamilies],
  );

  // Hydrate from selectedServiceId on first paint so back-nav from
  // Time/Contact/etc. lands on the family + cell that's actually in
  // the cart, instead of resetting to styles[0].
  const initial = useMemo(
    () => locateSelection(selectedServiceId, families, flatFamilies),
    // Compute once at mount; subsequent updates from back-nav are
    // handled by the keyed effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [activeStyleId, setActiveStyleId] = useState<string>(
    () => initial?.styleId ?? styles[0]?.family.id ?? '',
  );
  const active = styles.find((s) => s.family.id === activeStyleId) ?? styles[0];

  // Per-style axis state. When the user has no prior selection, fall
  // back to the active family's defaults so the matrix renders with
  // live prices on first paint instead of all "—" disabled cells.
  const [size, setSize] = useState<Size | null>(() => {
    if (initial?.size) return initial.size as Size;
    const firstActive = styles.find((s) => s.family.id === activeStyleId);
    return firstActive?.kind === 'matrix'
      ? pickDefaultSize(firstActive.family)
      : null;
  });
  const [length, setLength] = useState<Length | null>(() => {
    if (initial?.length) return initial.length as Length;
    const firstActive = styles.find((s) => s.family.id === activeStyleId);
    if (firstActive?.kind !== 'matrix') return null;
    const s = pickDefaultSize(firstActive.family);
    return pickDefaultLength(firstActive.family, s);
  });
  // M17/M18 — axis defaults + onSelect now happen inside the style/size/
  // length button onClick handlers (search this file for `pickStyle`,
  // `pickSize`, `pickLength`). The previous derived-state useEffects
  // re-ran every time activeStyleId/size/length changed and produced an
  // extra render cycle just to call setState/onSelect with values we
  // already knew at click time. Stale-closure risk on onSelect/onClear
  // also goes away because the handlers reference the latest props
  // synchronously.
  //
  // The single remaining mount-time onSelect mirrors the initial matrix
  // default into App state so the RunningBar shows a price on first
  // paint when the user lands fresh (no selectedServiceId yet). Guarded
  // by a ref so it fires exactly once. Latest onSelect ref pattern
  // avoids stale closures without re-running the effect.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const initialOnSelectFiredRef = useRef(false);
  useEffect(() => {
    if (initialOnSelectFiredRef.current) return;
    if (selectedServiceId) {
      initialOnSelectFiredRef.current = true;
      return;
    }
    if (!active || active.kind !== 'matrix') return;
    if (!size || !length) return;
    const cell = active.family.matrix[size]?.[length];
    if (cell) onSelectRef.current(cell.variationId);
    initialOnSelectFiredRef.current = true;
    // Intentionally mount-only — see comment above. Subsequent updates
    // are driven by the click handlers below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers used by the style/size/length buttons. Each computes the
  // resulting variation inline and forwards it via onSelect, replacing
  // what used to be a derived-state effect chain.
  function pickStyle(nextId: string) {
    if (activeStyleId === nextId) return;
    const next = styles.find((s) => s.family.id === nextId);
    setActiveStyleId(nextId);
    if (!next) return;
    if (next.kind === 'matrix') {
      const s = pickDefaultSize(next.family);
      const l = pickDefaultLength(next.family, s);
      setSize(s);
      setLength(l);
      if (s && l) {
        const cell = next.family.matrix[s]?.[l];
        if (cell) onSelect(cell.variationId);
      }
    } else {
      setSize(null);
      setLength(null);
      // Switching into a flat family that doesn't contain the
      // currently-highlighted variation must clear the selection —
      // otherwise Continue would submit the previous family's service
      // while the UI shows a different style as "active".
      const stillContained =
        selectedServiceId !== null &&
        next.family.services.some((s) => s.variationId === selectedServiceId);
      if (!stillContained && onClear) onClear();
    }
  }

  function pickSize(sz: Size) {
    if (!matrixActive) return;
    setSize(sz);
    const row = matrixActive.matrix[sz] ?? {};
    const nextLength: Length | null =
      length && row[length] ? length : pickDefaultLength(matrixActive, sz);
    if (nextLength !== length) setLength(nextLength);
    if (nextLength) {
      const cell = matrixActive.matrix[sz]?.[nextLength];
      if (cell) onSelect(cell.variationId);
    }
  }

  function pickLength(ln: Length) {
    if (!matrixActive || !size) return;
    setLength(ln);
    const cell = matrixActive.matrix[size]?.[ln];
    if (cell) onSelect(cell.variationId);
  }

  if (styles.length === 0) {
    return <div class="sbw-empty">No services configured.</div>;
  }

  const matrixActive = active?.kind === 'matrix' ? active.family : null;
  const flatActive = active?.kind === 'flat' ? active.family : null;

  // Disabled-state helpers for the segmented controls — a Size button
  // shouldn't even be tappable when the family doesn't have ANY length
  // available at that size.
  const sizeAvailable = (sz: Size): boolean => {
    if (!matrixActive) return false;
    const row = matrixActive.matrix[sz];
    if (!row) return false;
    return Object.keys(row).length > 0;
  };
  const lengthAvailable = (ln: Length): boolean => {
    if (!matrixActive || !size) return false;
    return matrixActive.matrix[size]?.[ln] != null;
  };

  // Live summary values for the matrix card.
  const cell =
    matrixActive && size && length
      ? matrixActive.matrix[size]?.[length]
      : null;

  return (
    <div class="sbw-scroll sbw-cfg">
      <div class="sbw-eyebrow">Pick your style</div>
      <h2 class="sbw-step-title">Build your booking.</h2>
      <p class="sbw-step-sub">
        Style, size, length. Three taps — price updates live.
      </p>

      <div class="sbw-cfg-styles">
        {styles.map(({ kind, family }) => {
          const isOn = activeStyleId === family.id;
          return (
            <button
              key={family.id}
              type="button"
              class={'sbw-cfg-style' + (isOn ? ' is-on' : '')}
              onClick={() => pickStyle(family.id)}
            >
              <div class="sbw-cfg-style-name">{family.name}</div>
              {family.tagline && (
                <div class="sbw-cfg-style-tag">{family.tagline}</div>
              )}
              <div class="sbw-cfg-style-foot">
                <span>from {formatPrice(family.fromCents)}</span>
                <span class="sbw-cfg-style-count">
                  {family.count} {kind === 'matrix' ? 'options' : kind === 'flat' && family.count === 1 ? 'service' : 'services'}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {matrixActive && (
        <>
          <div class="sbw-cfg-axis">
            <div class="sbw-cfg-axis-h">
              <span class="sbw-cfg-axis-eyebrow">Size</span>
            </div>
            <div class="sbw-cfg-seg">
              {FAMILY_SIZES.map((sz) => {
                const avail = sizeAvailable(sz);
                const isOn = size === sz;
                return (
                  <button
                    key={sz}
                    type="button"
                    class={
                      'sbw-cfg-seg-btn' +
                      (isOn ? ' is-on' : '') +
                      (!avail ? ' is-disabled' : '')
                    }
                    disabled={!avail}
                    onClick={() => avail && pickSize(sz)}
                  >
                    {sz}
                  </button>
                );
              })}
            </div>
          </div>

          <div class="sbw-cfg-axis">
            <div class="sbw-cfg-axis-h">
              <span class="sbw-cfg-axis-eyebrow">Length</span>
            </div>
            <div class="sbw-cfg-len">
              {FAMILY_LENGTHS.map((ln) => {
                const c = size ? matrixActive.matrix[size]?.[ln] : null;
                const avail = lengthAvailable(ln);
                const isOn = length === ln;
                return (
                  <button
                    key={ln}
                    type="button"
                    class={
                      'sbw-cfg-len-btn' +
                      (isOn ? ' is-on' : '') +
                      (!avail ? ' is-disabled' : '')
                    }
                    disabled={!avail}
                    onClick={() => avail && pickLength(ln)}
                  >
                    <span class="sbw-cfg-len-name">{ln}</span>
                    <span class="sbw-cfg-len-price">
                      {c ? formatPrice(c.priceCents) : '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {cell && (
            <div class="sbw-cfg-summary">
              <div class="sbw-cfg-summary-l">
                <div class="sbw-cfg-summary-eye">Your service</div>
                <div class="sbw-cfg-summary-name">
                  {size} {matrixActive.name} · {length}
                </div>
                {cell.durationMinutes > 0 && (
                  <div class="sbw-cfg-summary-meta">
                    ~{Math.round((cell.durationMinutes / 60) * 10) / 10}h in chair
                  </div>
                )}
              </div>
              <div class="sbw-cfg-summary-p">{formatPrice(cell.priceCents)}</div>
            </div>
          )}
        </>
      )}

      {flatActive && (
        <div class="sbw-cfg-locs">
          <div class="sbw-cfg-axis-h">
            <span class="sbw-cfg-axis-eyebrow">Pick a service</span>
          </div>
          {flatActive.services.map((s) => {
            const isOn = selectedServiceId === s.variationId;
            return (
              <button
                key={s.variationId}
                type="button"
                class={'sbw-cfg-loc' + (isOn ? ' is-on' : '')}
                onClick={() => onSelect(s.variationId)}
              >
                <div>
                  <div class="sbw-cfg-loc-name">{s.name}</div>
                  {s.durationMinutes > 0 && (
                    <div class="sbw-cfg-loc-meta">
                      {s.durationMinutes >= 60
                        ? `${Math.round((s.durationMinutes / 60) * 10) / 10}h`
                        : `${s.durationMinutes}m`}
                    </div>
                  )}
                </div>
                <div class="sbw-cfg-loc-price">
                  {formatPrice(s.priceCents)}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
